// Starfield backdrop — a Points shell far beyond the rink with per-star
// brightness and a gentle time-based twinkle. Positions are seeded
// deterministically (same sky every boot; this is presentation, not sim,
// but determinism keeps captures comparable).

import { attribute, time, uniform, vec3 } from "three/tsl";
import { BufferGeometry, Float32BufferAttribute, Points, PointsNodeMaterial } from "three/webgpu";
import type { SpaceStyle } from "../style/style-schema.js";

function buildUniforms(style: SpaceStyle) {
  return {
    brightness: uniform(style.brightness),
    twinkleAmount: uniform(style.twinkleAmount),
    twinkleRate: uniform(style.twinkleRate),
  };
}

export type StarfieldUniforms = ReturnType<typeof buildUniforms>;

export function applyStarfieldStyle(u: StarfieldUniforms, s: SpaceStyle): void {
  u.brightness.value = s.brightness;
  u.twinkleAmount.value = s.twinkleAmount;
  u.twinkleRate.value = s.twinkleRate;
}

/** Build the full starfield Points object (geometry + material together —
 * star count and shell radius are build-time; rebuild via the lab to change). */
export function createStarfield(style: SpaceStyle): {
  points: Points;
  u: StarfieldUniforms;
} {
  const u = buildUniforms(style);

  // mulberry32 — deterministic star placement.
  let seed = 0x5eed5;
  const rand = (): number => {
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  const positions: number[] = [];
  const seeds: number[] = [];
  for (let i = 0; i < style.starCount; i++) {
    // Uniform on a sphere shell, biased to the upper hemisphere so most
    // stars sit behind/above the rink rather than under the floor.
    const z = rand() * 1.6 - 0.6;
    const phi = rand() * Math.PI * 2;
    const r = Math.sqrt(Math.max(0, 1 - z * z));
    positions.push(
      Math.cos(phi) * r * style.shellRadius,
      z * style.shellRadius,
      Math.sin(phi) * r * style.shellRadius,
    );
    seeds.push(rand());
  }

  const geo = new BufferGeometry();
  geo.setAttribute("position", new Float32BufferAttribute(positions, 3));
  geo.setAttribute("aSeed", new Float32BufferAttribute(seeds, 1));

  const material = new PointsNodeMaterial({
    size: style.starSize,
    sizeAttenuation: false,
    transparent: true,
    depthWrite: false,
  });
  // biome-ignore lint/suspicious/noExplicitAny: TSL graph-building value
  const starSeed = attribute("aSeed", "float") as any;
  const twinkle = time
    .mul(u.twinkleRate)
    .add(starSeed.mul(40))
    .sin()
    .mul(0.5)
    .add(0.5)
    .mul(u.twinkleAmount)
    .add(u.twinkleAmount.oneMinus());
  // Brightness spread: most stars dim, a few hot (seed^3 skew).
  const perStar = starSeed.mul(starSeed).mul(starSeed).mul(1.5).add(0.15);
  material.colorNode = vec3(0.82, 0.88, 1).mul(u.brightness.mul(perStar).mul(twinkle));

  const points = new Points(geo, material);
  points.frustumCulled = false;
  return { points, u };
}
